import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CardContent, Grid, Container, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { AppWidgetSummary } from '../sections/@dashboard/app';
import { MapComponent } from '../components/map';
import { useProject } from '../hooks/ProjectContext';

function formatLastSeen(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

function parseCoords(str) {
  if (!str) return null;
  const parts = str.split(',').map(s => parseFloat(s.trim()));
  if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1]))
    return { lat: parts[0], lng: parts[1] };
  return null;
}

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return tkn ? { Authorization: `Bearer ${tkn}` } : {};
}

// ----------------------------------------------------------------------

export default function DashboardAppPage() {
  const navigate = useNavigate();
  const { selectedProject, selectedSite } = useProject();
  const [deviceRegistry, setDeviceRegistry] = useState([]);
  const [stats, setStats] = useState({ devices: 0, users: 0, sites: 0, active24h: 0 });
  const [focusDevice, setFocusDevice] = useState(null);
  const [activityWindow, setActivityWindow] = useState('24h');

  const loadDeviceRegistry = useCallback(async (windowArg) => {
    try {
      const w = windowArg || activityWindow;
      const r = await fetch(`/api/admin/devices?window=${encodeURIComponent(w)}`, { headers: bearerHeaders() });
      const j = await r.json();
      setDeviceRegistry(Array.isArray(j.devices) ? j.devices : []);
    } catch {
      setDeviceRegistry([]);
    }
  }, [activityWindow]);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/stats');
      const j = await r.json();
      if (j?.ok) setStats({ devices: j.devices || 0, users: j.users || 0, sites: j.sites || 0, active24h: j.active24h || 0 });
    } catch {}
  }, []);

  useEffect(() => {
    loadDeviceRegistry();
    loadStats();
  }, [loadDeviceRegistry, loadStats]);

  const handleWindowChange = (_e, next) => {
    if (!next) return; // ignore null (toggle deselect)
    setActivityWindow(next);
    loadDeviceRegistry(next);
  };

  // Filter by header scope selector
  const filteredDevices = useMemo(() => {
    let list = deviceRegistry;
    if (selectedProject) list = list.filter((d) => d.project === selectedProject.name);
    if (selectedSite) list = list.filter((d) => d.site === selectedSite.name);
    return list;
  }, [deviceRegistry, selectedProject, selectedSite]);

  return (
    <>
      <Helmet>
        <title> Dashboard | TERRRA </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 5 }}>
          <Button size="small" variant="outlined" onClick={() => window.open('https://grafana-staging.terrra.one', '_blank')}>Grafana</Button>
          <Button size="small" variant="outlined" onClick={() => { loadDeviceRegistry(); loadStats(); }}>Refresh</Button>
        </Stack>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
            <AppWidgetSummary title="Active Devices" total={Number(stats.active24h)} color="info" subtitle="last 24h" icon={'mdi:antenna'} sx={{ flex: 1 }} />
          </Grid>

          <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
            <AppWidgetSummary title="Total Devices" total={Number(stats.devices)} subtitle="all time" icon={'mdi:router-wireless'} sx={{ flex: 1 }} />
          </Grid>

          <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
            <AppWidgetSummary title="Users" total={Number(stats.users)} color="warning" subtitle="registered" icon={'mdi:account-multiple'} sx={{ flex: 1 }} />
          </Grid>

          <Grid item xs={6} sm={3} sx={{ display: 'flex' }}>
            <AppWidgetSummary title="Sites" total={Number(stats.sites)} color="error" subtitle="active" icon={'mdi:map-marker-multiple'} sx={{ flex: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6">
                    Devices
                    {selectedProject ? ` \u2014 ${selectedProject.name}${selectedSite ? ` / ${selectedSite.name}` : ''}` : ''}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({filteredDevices.length})
                    </Typography>
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive value={activityWindow} onChange={handleWindowChange} aria-label="Activity window">
                    <ToggleButton value="now" aria-label="last 10 minutes">Now</ToggleButton>
                    <ToggleButton value="24h" aria-label="last 24 hours">24h</ToggleButton>
                    <ToggleButton value="week" aria-label="last 7 days">Week</ToggleButton>
                    <ToggleButton value="all" aria-label="all time">All</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 70px 110px 110px', gap: '0 8px', overflowX: 'auto' }}>
                  {['MAC', 'Project', 'Site', 'Ver', 'Last seen', ''].map((h) => (
                    <Typography key={h} variant="caption" color="text.secondary"
                      sx={{ fontWeight: 700, pb: 0.5, borderBottom: '2px solid rgba(145,158,171,0.3)', whiteSpace: 'nowrap' }}>
                      {h}
                    </Typography>
                  ))}
                  {filteredDevices.length ? filteredDevices.map((d) => {
                    return [
                      <Typography key={`${d.wfmc}-mac`} variant="caption"
                        sx={{ fontWeight: 600, py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          ...(parseCoords(d.location) ? { cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } } : {}) }}
                        onClick={() => { const c = parseCoords(d.location); if (c) setFocusDevice(c); }}>
                        {d.wfmc || '\u2014'}
                      </Typography>,
                      <Typography key={`${d.wfmc}-proj`} variant="caption" color="text.secondary" sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.project || '\u2014'}</Typography>,
                      <Typography key={`${d.wfmc}-site`} variant="caption" sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.site || '\u2014'}</Typography>,
                      <Typography key={`${d.wfmc}-ver`} variant="caption" color="text.secondary" sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)' }}>{d.last_version || '\u2014'}</Typography>,
                      <Typography key={`${d.wfmc}-seen`} variant="caption" color="text.secondary" sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)', whiteSpace: 'nowrap' }}>{formatLastSeen(d.last_seen)}</Typography>,
                      <Box key={`${d.wfmc}-act`} sx={{ py: 0.5, borderBottom: '1px solid rgba(145,158,171,0.16)' }}>
                        <Button size="small" variant="outlined" onClick={() => navigate(`/admin/device/${d.wfmc}`)}>Manage &rarr;</Button>
                      </Box>,
                    ];
                  }) : (
                    <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', mt: 1 }}>
                      {deviceRegistry.length === 0 ? `No devices seen in the ${activityWindow === 'now' ? 'last 10 min' : activityWindow === '24h' ? 'last 24h' : activityWindow === 'week' ? 'last week' : 'system'}.` : 'No devices for this selection.'}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={100}>
            <MapComponent focusDevice={focusDevice} />
          </Grid>

        </Grid>
      </Container>
    </>
  );
}
